<template>
  <div class="user-stats">
    <h3 class="typo-headline-6">Tu Actividad</h3>
    <div v-if="loading" class="loading-stats typo-body-2">
      Cargando tu actividad...
    </div>
    <div v-else-if="error" class="error-stats typo-body-2">
      {{ error }}
    </div>
    <div class="stats-content" v-else-if="isLoggedIn">
      <div class="stat-item clickable pad" @click="goToUserActivityTab('threads')">
        <span class="stat-label">Hilos creados:</span>
        <span class="stat-value">
          {{ userStats.threadsCreated }}
        </span>
      </div>
      <div class="stat-item clickable pad" @click="goToUserActivityTab('comments')">
        <span class="stat-label">Comentarios:</span>
        <span class="stat-value">
          {{ userStats.commentsPosted }}
        </span>
      </div>
      <div class="stat-item clickable pad" @click="goToUserActivityTab('favorites')">
        <span class="stat-label">Favoritos:</span>
        <span class="stat-value">
          {{ userStats.favorites }}
        </span>
      </div>
      <div class="stat-item clickable pad" @click="goToUserActivityTab('notifications')">
        <span class="stat-label">Notificaciones:</span>
        <span class="stat-value notification-link">
          {{ userStats.notifications }}
          <span v-if="hasUnreadNotifications" class="notification-dot"></span>
        </span>
      </div>
      <div class="stat-item pad">
        <span class="stat-label">Reputación:</span>
        <span class="stat-value" :class="getReputationClass">{{ userStats.reputation }}</span>
      </div>
      <button class="activity-btn typo-button" @click="goToUserActivity">
        <i class="material-icons">person</i>
        Mi Actividad
      </button>
      <button class="create-thread-btn typo-button" v-if="canWrite" @click="createNewThread">
        <i class="material-icons">add</i>
        Crear Nuevo Hilo
      </button>
    </div>
    <div class="login-prompt" v-else>
      <p class="typo-body-2">Inicia sesión para participar en el foro</p>
      <button class="login-btn typo-button" @click="goToLogin">Iniciar Sesión</button>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
const axios = forumApi;
import { mapGetters } from 'vuex';

export default {
  name: 'UserActivityWidget',
  data() {
    return {
      userStats: {
        threadsCreated: 0,
        commentsPosted: 0,
        favorites: 0,
        notifications: 0,
        unsilencedNotifications: 0,
        reputation: 0
      },
      loading: true,
      error: null,
    };
  },
  computed: {
    ...mapGetters(['_g_IsUserLogged', '_g_User']),
    isLoggedIn() {
      return this._g_IsUserLogged;
    },
    canWrite() {
      const user = this._g_User;
      return !!(user && (user.is_admin_user || user.is_forum_writer_user));
    },
    getReputationClass() {
      if (this.userStats.reputation > 0) {
        return 'positive-reputation';
      } else if (this.userStats.reputation < 0) {
        return 'negative-reputation';
      }
      return '';
    },
    hasUnreadNotifications() {
      return this.userStats.unsilencedNotifications > 0;
    }
  },
  mounted() {
    if (this.isLoggedIn) {
      this.fetchUserActivity();
    } else {
      this.loading = false;
    }
    
    // Listen for activity update events
    this.$root.$on('userActivityUpdated', this.handleActivityUpdate);
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off('userActivityUpdated', this.handleActivityUpdate);
  },
  methods: {
    fetchUserActivity() {
      // Reset loading state
      this.loading = true;
      this.error = null;
      
      // Create an array of promises for parallel requests
      const promises = [
        this.fetchThreadsCount(),
        this.fetchCommentsCount(),
        this.fetchFavoritesCount(),
        this.fetchNotificationsCount(),
        this.fetchUserKarma()
      ];
      
      // Wait for all requests to complete
      Promise.all(promises)
        .then(() => {
          this.loading = false;
        })
        .catch(err => {
          console.error('Error fetching user activity:', err);
          this.error = 'Error al cargar tu actividad';
          this.loading = false;
        });
    },
    fetchThreadsCount() {
      return axios.get('/forum/api/user/threads?limit=1')
        .then(response => {
          if (response.data && response.data.pagination) {
            this.userStats.threadsCreated = response.data.pagination.total;
          }
        });
    },
    fetchCommentsCount() {
      return axios.get('/forum/api/user/comments?limit=1')
        .then(response => {
          if (response.data && response.data.pagination) {
            this.userStats.commentsPosted = response.data.pagination.total;
          }
        });
    },
    fetchFavoritesCount() {
      return axios.get('/forum/api/user/favorites?limit=1')
        .then(response => {
          if (response.data && response.data.pagination) {
            this.userStats.favorites = response.data.pagination.total;
          } else {
            throw new Error('Invalid response format');
          }
        })
        .catch(err => {
          console.error('Error fetching favorites count:', err);
          this.userStats.favorites = 0;
        });
    },
    fetchNotificationsCount() {
      return axios.get('/forum/api/notifications')
        .then(response => {
          if (response.data) {
            this.userStats.notifications = response.data.total_count || 0;
            this.userStats.unsilencedNotifications = response.data.unsilenced_count || 0;
          }
        })
        .catch(err => {
          console.error('Error fetching notifications count:', err);
          this.userStats.notifications = 0;
          this.userStats.unsilencedNotifications = 0;
        });
    },
    fetchUserKarma() {
      return axios.get('/api/forum-karma/user/me')
        .then(response => {
          if (response.data) {
            this.userStats.reputation = response.data.karma;
          }
        });
    },
    handleActivityUpdate(updateType) {
      // Refresh the relevant stat when activity is updated
      switch (updateType) {
        case 'comment':
          this.fetchCommentsCount();
          break;
        case 'favorite':
          this.fetchFavoritesCount();
          break;
        case 'thread':
          this.fetchThreadsCount();
          break;
        case 'notification':
          this.fetchNotificationsCount();
          break;
        case 'all':
        default:
          this.fetchUserActivity();
          break;
      }
    },
    goToUserActivity() {
      // Check if we're already on the UserActivity page
      if (this.$route.name === 'UserActivity') {
        // If we're already on the page, just change the tab
        this.$root.$emit('changeUserActivityTab', 'comments');
      } else {
        // Navigate to the page with the tab
        this.$router.push({ name: 'UserActivity', query: { tab: 'comments' } });
      }
    },
    // Modified navigation methods to handle tab switching on the same page
    goToUserActivityTab(tab) {
      if (this.$route.name === 'UserActivity') {
        // If we're already on the UserActivity page, emit event to change tab
        this.$root.$emit('changeUserActivityTab', tab);
      } else {
        // Navigate to the page with the specified tab
        this.$router.push({ name: 'UserActivity', query: { tab } });
      }
    },
    createNewThread() {
      this.$router.push({ name: 'ForumNewThread' });
    },
    goToLogin() {
      // Redirect to login page
      window.location.href = '/user/login';
    }
  }
};
</script>

<style scoped>
.user-stats {
  background-color: var(--color-surface);
  padding: 20px;
  border-radius: 15px;
  color: var(--color-text-primary);
}

.user-stats h3 {
  margin-top: 0;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-outline);
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
}

.stat-item.clickable {
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.3s ease;
}

.stat-item.pad {
  padding: 8px 12px;
}

.stat-item.clickable:hover {
  background-color: var(--color-active-surface, #f5f5f5);
}

.stat-label {
  color: var(--color-text-secondary);
}

.stat-value {
  font-weight: 500;
  color: var(--color-highlight);
}

.positive-reputation {
  color: #4caf50;
}

.negative-reputation {
  color: #f44336;
}

.activity-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background-color: var(--color-surface-variant, #e3f2fd);
  color: var(--color-highlight);
  border: 1px solid var(--color-highlight);
  padding: 10px;
  border-radius: 4px;
  margin-top: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%;
}

.activity-btn:hover {
  background-color: var(--color-surface-hover, #d0e8f7);
}

.create-thread-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border: none;
  padding: 10px;
  border-radius: 4px;
  margin-top: 10px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 100%;
}

.create-thread-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.login-prompt {
  text-align: center;
}

.login-btn {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  margin-top: 10px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.login-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.loading-stats,
.error-stats {
  text-align: center;
  padding: 10px 0;
}

.error-stats {
  color: #f44336;
}

.notification-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.notification-dot {
  width: 8px;
  height: 8px;
  background-color: #f44336;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style> 